import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, CheckCircle2, ArrowLeft, ArrowRight, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES } from "../../constants/brand";
import { MOCK_QUOTES } from "../../data/mock";
import UPIPaymentModal from "../../components/shared/UPIPaymentModal";

const STEPS = ["Product", "Upload", "Quotes", "Payment", "Confirm"];

export default function CustomerNewOrder() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [product, setProduct] = useState({ category: null, quantity: 500, size: "Standard", finish: "Matt", delivery: "Standard" });
  const [file, setFile] = useState(null);
  const [chosenVendor, setChosenVendor] = useState(null);
  const [showPay, setShowPay] = useState(false);
  const [orderId] = useState(`EP-${Math.floor(2000 + Math.random() * 1000)}`);

  const est = useMemo(() => {
    const base = 3.5 * product.quantity;
    const delivery = product.delivery === "Express" ? 100 : product.delivery === "Same-day" ? 200 : 0;
    const total = Math.round((base + delivery) * 1.18);
    return { base: Math.round(base), delivery, total };
  }, [product]);

  return (
    <div data-testid="customer-new-order">
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">New Order</div>
        <h1 className="font-display text-3xl font-bold">Start a print order</h1>
      </div>

      {/* Progress */}
      <div className="mb-6 flex items-center gap-2 overflow-x-auto hide-scrollbar">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold ${i === step ? "border-neutral-900 bg-neutral-950 text-white" : i < step ? "border-lime-500 bg-lime-100 text-lime-800" : "border-neutral-200 text-neutral-500"}`} data-testid={`step-${i}`}>
              <span className="font-mono">{String(i + 1).padStart(2, "0")}</span>
              {s}
            </div>
            {i < STEPS.length - 1 && <div className="h-px w-6 bg-neutral-300" />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2">
          {step === 0 && (
            <section className="rounded-lg border border-neutral-200 bg-white p-5" data-testid="step-product">
              <h2 className="font-display text-xl font-bold">Choose product</h2>
              <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3">
                {CATEGORIES.slice(0, 9).map((c) => (
                  <button key={c.key} onClick={() => setProduct({ ...product, category: c.key })} className={`rounded-lg border p-3 text-left ${product.category === c.key ? "border-neutral-900 bg-lime-100" : "border-neutral-200 hover:border-neutral-900"}`} data-testid={`no-cat-${c.key}`}>
                    <div className="text-xs uppercase tracking-widest text-neutral-500">From {c.price}</div>
                    <div className="font-semibold">{c.label}</div>
                  </button>
                ))}
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-3">
                <Select label="Quantity" testid="no-qty" value={product.quantity} onChange={(v) => setProduct({ ...product, quantity: Number(v) })} options={[100, 250, 500, 1000, 2000]} />
                <Select label="Size" testid="no-size" value={product.size} onChange={(v) => setProduct({ ...product, size: v })} options={["Standard", "A5", "A4", "A3", "Custom"]} />
                <Select label="Finish" testid="no-finish" value={product.finish} onChange={(v) => setProduct({ ...product, finish: v })} options={["Matt", "Gloss", "UV", "Textured"]} />
              </div>

              <div className="mt-4">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Delivery</label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {["Standard", "Express", "Same-day"].map((d) => (
                    <button key={d} onClick={() => setProduct({ ...product, delivery: d })} className={`rounded-md border px-3 py-2 text-sm font-semibold ${product.delivery === d ? "border-neutral-900 bg-lime-100" : "border-neutral-200 hover:border-neutral-900"}`} data-testid={`no-delivery-${d.toLowerCase()}`}>{d}</button>
                  ))}
                </div>
              </div>
            </section>
          )}

          {step === 1 && (
            <section className="rounded-lg border border-neutral-200 bg-white p-5" data-testid="step-upload">
              <h2 className="font-display text-xl font-bold">Upload your design</h2>
              <label className="mt-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 p-10 text-center hover:border-lime-500" data-testid="no-upload-label">
                <UploadCloud size={36} className="text-lime-500" />
                <p className="mt-3 text-sm font-semibold">Drag & drop your file, or click to browse</p>
                <p className="mt-1 text-xs text-neutral-500">PDF, AI, CDR, PSD, PNG, JPG · up to 100MB</p>
                <input type="file" className="hidden" accept=".pdf,.ai,.cdr,.psd,.png,.jpg,.jpeg" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="no-upload-input" />
              </label>
              {file && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-lime-300 bg-lime-50 p-3 text-sm" data-testid="no-file-info">
                  <CheckCircle2 size={16} className="text-lime-600" /> {file.name} · {(file.size / 1024 / 1024).toFixed(1)}MB
                </div>
              )}
              <div className="mt-4">
                <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Special instructions</label>
                <textarea rows={3} className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none focus:ring-2 focus:ring-lime-400" placeholder="Any specific notes for the vendor…" data-testid="no-instructions" />
              </div>
            </section>
          )}

          {step === 2 && (
            <section className="rounded-lg border border-neutral-200 bg-white p-5" data-testid="step-quotes">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-bold">Compare live quotes</h2>
                <div className="rounded-full border border-neutral-900 bg-lime-100 px-3 py-1 text-xs font-mono font-bold">Quotes valid: 01:24:12</div>
              </div>
              <p className="mt-1 text-xs text-neutral-500">3 verified vendors responded. Pick one to continue.</p>

              <div className="mt-4 space-y-3">
                {MOCK_QUOTES[0].vendors.map((v) => (
                  <div key={v.name} className={`flex flex-col justify-between gap-3 rounded-lg border p-4 md:flex-row md:items-center ${chosenVendor === v.name ? "border-neutral-900 bg-lime-50" : "border-neutral-200"}`} data-testid={`no-quote-${v.name.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div>
                      <div className="flex items-center gap-2 font-display font-bold">{v.name} <span className="rounded-full bg-lime-400 px-2 py-0.5 text-[10px] font-bold text-neutral-950">{v.badge}</span></div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-neutral-500">
                        <span className="flex items-center gap-1 text-lime-600"><Star size={12} fill="currentColor" strokeWidth={0} /> {v.rating}</span>
                        <span>{v.distance}</span>
                        <span>Ready by {v.ready}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="font-mono text-2xl font-bold">₹{v.price.toLocaleString("en-IN")}</div>
                      <button onClick={() => setChosenVendor(v.name)} className="rounded-md bg-neutral-950 px-3 py-2 text-xs font-bold text-white hover:bg-neutral-800" data-testid={`no-quote-select-${v.name.replace(/\s+/g, "-").toLowerCase()}`}>Select</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {step === 3 && (
            <section className="rounded-lg border border-neutral-200 bg-white p-5" data-testid="step-payment">
              <h2 className="font-display text-xl font-bold">Payment</h2>
              <div className="mt-4 space-y-2 text-sm">
                <Row label="Base amount" v={`₹${est.base.toLocaleString("en-IN")}`} />
                <Row label="Delivery" v={`₹${est.delivery}`} />
                <Row label="GST (18%)" v={`₹${(est.total - est.base - est.delivery).toLocaleString("en-IN")}`} />
                <Row label="Total" v={`₹${est.total.toLocaleString("en-IN")}`} bold />
              </div>
              <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="no-escrow-notice">
                <Sparkles size={14} className="mr-1 inline text-amber-600" /> Your payment is held in escrow. Released to vendor only after you confirm delivery.
              </div>
              <button onClick={() => setShowPay(true)} className="btn-offset mt-6 w-full rounded-md bg-lime-400 px-4 py-3 text-sm font-bold text-neutral-950 hover:bg-lime-300" data-testid="no-pay-btn">
                Pay via UPI — ₹{est.total.toLocaleString("en-IN")}
              </button>
              <UPIPaymentModal open={showPay} onClose={() => { setShowPay(false); setStep(4); toast.success("Order placed!"); }} amount={est.total} orderRef={orderId} />
            </section>
          )}

          {step === 4 && (
            <section className="rounded-lg border border-neutral-200 bg-white p-8 text-center" data-testid="step-confirm">
              <CheckCircle2 size={56} className="mx-auto text-lime-500" />
              <h2 className="mt-4 font-display text-2xl font-bold">Order placed!</h2>
              <p className="mt-2 text-sm text-neutral-600">Order ID <span className="font-mono font-bold">{orderId}</span> with {chosenVendor || "your selected vendor"}.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <button onClick={() => nav("/customer/track")} className="rounded-md bg-neutral-950 px-4 py-2 text-sm font-bold text-white hover:bg-neutral-800" data-testid="no-track">Track My Order</button>
                <button onClick={() => { setStep(0); setProduct({ category: null, quantity: 500, size: "Standard", finish: "Matt", delivery: "Standard" }); setChosenVendor(null); }} className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold hover:border-neutral-900" data-testid="no-again">Order something else</button>
              </div>
            </section>
          )}

          {step < 4 && (
            <div className="mt-4 flex items-center justify-between">
              <button disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-3 py-2 text-sm font-semibold hover:border-neutral-900 disabled:opacity-50" data-testid="no-back"><ArrowLeft size={14} /> Back</button>
              <button
                onClick={() => {
                  if (step === 0 && !product.category) { toast.error("Pick a product"); return; }
                  if (step === 1 && !file) { toast.error("Upload a file first"); return; }
                  if (step === 2 && !chosenVendor) { toast.error("Select a vendor to continue"); return; }
                  setStep((s) => Math.min(4, s + 1));
                }}
                className="inline-flex items-center gap-1 rounded-md bg-neutral-950 px-4 py-2 text-sm font-bold text-white hover:bg-neutral-800"
                data-testid="no-next"
              >
                Continue <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Sticky estimate */}
        <aside className="rounded-lg border border-neutral-200 bg-white p-5" data-testid="no-estimate">
          <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Live estimate</div>
          <div className="mt-2 font-display text-4xl font-black">₹{est.total.toLocaleString("en-IN")}</div>
          <div className="mt-1 text-xs text-neutral-500">Incl. GST · {product.delivery} delivery</div>
          <div className="mt-4 space-y-1 text-xs text-neutral-600">
            <div className="flex items-center gap-1"><Sparkles size={12} className="text-lime-500" /> AI file check</div>
            <div className="flex items-center gap-1"><CheckCircle2 size={12} className="text-lime-500" /> Escrow payment</div>
            <div className="flex items-center gap-1"><CheckCircle2 size={12} className="text-lime-500" /> Live tracking</div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Select({ label, options, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-neutral-600">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm" data-testid={testid}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Row({ label, v, bold }) {
  return (
    <div className={`flex items-center justify-between border-b border-neutral-100 py-2 ${bold ? "font-bold text-lg pt-4" : ""}`}>
      <span className={bold ? "font-display" : "text-neutral-600"}>{label}</span>
      <span className={bold ? "font-mono text-2xl" : "font-mono"}>{v}</span>
    </div>
  );
}
